import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { GCSStorage as Storage } from "../../storage/gcs";
import { useSnackbar } from "notistack";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";

import { downloadBlob } from "../Vault";

const TEAL = "#008e92";

export type VaultListFile = {
  key: string;
  name: string;
  size?: number;
};

function fileNameFromKey(key: string): string {
  try {
    return (
      new URL(key, "https://example.com").pathname.split("/").pop() || key
    );
  } catch {
    return key.split("/").pop() || key;
  }
}

function formatFileSize(bytes?: number): string {
  if (bytes == null || Number.isNaN(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function FileTypeIcon({ name }: { name: string }) {
  const lower = name.toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower)) {
    return <ImageOutlinedIcon sx={{ color: "#42a5f5", fontSize: 22 }} />;
  }
  if (/\.pdf$/.test(lower)) {
    return <PictureAsPdfOutlinedIcon sx={{ color: "#e53935", fontSize: 22 }} />;
  }
  if (/\.(xlsx?|csv|ods|tsv)$/.test(lower)) {
    return <TableChartOutlinedIcon sx={{ color: "#43a047", fontSize: 22 }} />;
  }
  if (/\.(docx?|rtf|odt|txt)$/.test(lower)) {
    return <ArticleOutlinedIcon sx={{ color: "#1e88e5", fontSize: 22 }} />;
  }
  if (/\.(pptx?|odp)$/.test(lower)) {
    return <DescriptionOutlinedIcon sx={{ color: "#fb8c00", fontSize: 22 }} />;
  }
  return (
    <InsertDriveFileOutlinedIcon sx={{ color: "#78909c", fontSize: 22 }} />
  );
}

/** Build the usual single-folder vault prefix: `{module}/{id}/`. */
export function vaultPath(
  moduleFolder: string,
  id?: number | string | Number
): string {
  return `${moduleFolder}/${id}/`;
}

/**
 * List vault objects under one or more prefixes (same filter rules as Vault.tsx).
 * Call only on demand — do not prefetch for every list row.
 */
export async function listVaultFiles(
  paths: string[]
): Promise<VaultListFile[]> {
  const lists = await Promise.all(paths.map((path) => Storage.list(path)));
  const out: VaultListFile[] = [];

  lists.forEach((results, idx) => {
    const path = paths[idx];
    const rows = Array.isArray(results)
      ? results
      : ((results as { results?: unknown[] })?.results ?? []);

    for (const result of rows as Array<{ key?: string; size?: number }>) {
      if (!result.size || !result.key) continue;
      const keyPrefix = result.key.split("/").slice(0, -1).join("/") + "/";
      if (keyPrefix !== path) continue;
      out.push({
        key: result.key,
        name: fileNameFromKey(result.key),
        size: result.size,
      });
    }
  });

  return out;
}

// In-memory cache across row re-renders to avoid redundant GCS queries
const vaultFilesCache = new Map<string, VaultListFile[]>();

export function clearVaultFilesCache(): void {
  vaultFilesCache.clear();
}

type Props = {
  /** Storage prefixes used by the module's Documents/Files tab Vault(s). */
  paths: string[];
  /** Record id — used to open the detail/Files tab when footer is clicked. */
  recordId?: number | string;
  /** Override navigation when "Open Files Tab" is clicked. */
  onOpenFilesTab?: () => void;
  /**
   * From list API `has_files`. When false, show "—" (no dropdown).
   * When undefined (module not wired yet), keep showing the Files control.
   */
  hasFiles?: boolean;
};

/**
 * Popover Files cell for Documents/Files-tab vault files.
 * Matches the canonical Aeronet pattern:
 * - When 0 files exist: displays muted em dash "—" (no link or dropdown).
 * - When files exist: displays "${count} file${s} ▼" in brand teal with popover on click.
 */
export default function VaultFilesCell({
  paths,
  recordId,
  onOpenFilesTab,
  hasFiles,
}: Props) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const cacheKey = paths.slice().sort().join("|");

  const [files, setFiles] = useState<VaultListFile[] | null>(() => {
    if (!paths.length || hasFiles === false) return [];
    if (vaultFilesCache.has(cacheKey)) {
      return vaultFilesCache.get(cacheKey)!;
    }
    return null;
  });

  // Check files on mount if not in cache
  useEffect(() => {
    if (hasFiles === false || !paths.length) {
      return;
    }
    if (vaultFilesCache.has(cacheKey)) {
      setFiles(vaultFilesCache.get(cacheKey)!);
      return;
    }
    let isMounted = true;
    setLoading(true);
    listVaultFiles(paths)
      .then((loaded) => {
        vaultFilesCache.set(cacheKey, loaded);
        if (isMounted) setFiles(loaded);
      })
      .catch(() => {
        if (isMounted) setFiles([]);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
    // `cacheKey` is the content key of `paths`; the array itself is rebuilt by each grid render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, hasFiles]);

  const open = Boolean(anchorEl);

  const ensureLoaded = useCallback(async () => {
    if (files !== null || loading) return;
    if (!paths.length) {
      setFiles([]);
      return;
    }
    setLoading(true);
    try {
      const loaded = await listVaultFiles(paths);
      vaultFilesCache.set(cacheKey, loaded);
      setFiles(loaded);
    } catch {
      enqueueSnackbar("Failed to load files", { variant: "error" });
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [cacheKey, files, loading, paths, enqueueSnackbar]);

  const handleOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    void ensureLoaded();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadFile = async (file: VaultListFile) => {
    setDownloadingKey(file.key);
    try {
      // Amplify's TS types say `string`, but `{ download: true }` returns
      // `{ Body }` at runtime (same path Vault.tsx uses).
      const result: unknown = await Storage.get(file.key, { download: true });
      const blob =
        typeof result === "string"
          ? await (await fetch(result)).blob()
          : (result as { Body: Blob }).Body;
      downloadBlob(blob, file.name);
    } catch {
      enqueueSnackbar("Failed to download file", { variant: "error" });
    } finally {
      setDownloadingKey(null);
    }
  };

  const openFilesTab = (e: MouseEvent) => {
    e.stopPropagation();
    handleClose();
    if (onOpenFilesTab) {
      onOpenFilesTab();
      return;
    }
    if (recordId != null) {
      // Detail screens honour `?tab=files` via useTabFromQuery.
      navigate(`${recordId}?tab=files`);
    }
  };

  // Canonical empty state: when 0 files exist, render em dash without any link.
  if (hasFiles === false || (files !== null && files.length === 0)) {
    return (
      <Box
        component="span"
        aria-label="No files"
        data-testid="vault-files-empty"
        sx={{ color: "text.disabled", userSelect: "none" }}
      >
        —
      </Box>
    );
  }

  // Still resolving files for this row: render muted em dash placeholder
  if (files === null) {
    return (
      <Box
        component="span"
        aria-label="No files"
        sx={{ color: "text.disabled", userSelect: "none" }}
      >
        —
      </Box>
    );
  }

  const count = files.length;
  const label = `${count} file${count === 1 ? "" : "s"}`;

  return (
    <>
      <Link
        component="button"
        type="button"
        underline="none"
        onClick={handleOpen}
        data-testid="vault-files-trigger"
        aria-label={`${count} attached files`}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          color: TEAL,
          fontSize: "0.875rem",
          fontWeight: 500,
          cursor: "pointer",
          border: 0,
          background: "none",
          p: 0,
          fontFamily: "inherit",
          "&:hover": { color: TEAL, textDecoration: "underline" },
        }}
      >
        {label}
        <ArrowDropDownIcon
          sx={{
            fontSize: 18,
            color: TEAL,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.15s",
          }}
        />
      </Link>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            width: 320,
            mt: 0.5,
            borderRadius: 1.5,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Files{count != null ? ` (${count})` : ""}
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} sx={{ color: TEAL }} />
            </Box>
          )}
          {!loading && files !== null && files.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 2 }}
            >
              No files
            </Typography>
          )}
          {!loading &&
            (files ?? []).map((f) => (
              <Stack
                key={f.key}
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{
                  px: 2,
                  py: 1.25,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <FileTypeIcon name={f.name} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    title={f.name}
                    sx={{ fontWeight: 500, lineHeight: 1.3 }}
                  >
                    {f.name}
                  </Typography>
                  {f.size != null && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1.2 }}
                    >
                      {formatFileSize(f.size)}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Download ${f.name}`}
                  disabled={downloadingKey === f.key}
                  onClick={(e) => {
                    e.stopPropagation();
                    void downloadFile(f);
                  }}
                  sx={{ color: TEAL }}
                >
                  {downloadingKey === f.key ? (
                    <CircularProgress size={16} sx={{ color: TEAL }} />
                  ) : (
                    <DownloadIcon fontSize="small" />
                  )}
                </IconButton>
              </Stack>
            ))}
        </Box>

        {(onOpenFilesTab || recordId != null) && (
          <>
            <Divider />
            <Box
              component="button"
              type="button"
              onClick={openFilesTab}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                px: 2,
                py: 1.25,
                border: 0,
                background: "none",
                cursor: "pointer",
                color: TEAL,
                fontFamily: "inherit",
                fontSize: "0.875rem",
                fontWeight: 600,
                textAlign: "left",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <FolderOutlinedIcon sx={{ fontSize: 20 }} />
              Open Files Tab &gt;
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}

type FilesColumnOpts<Row> = {
  /** Resolve Vault storage prefix(es) for the row. */
  getPaths: (row: Row) => string[];
  /** Optional — open the record's Files tab (defaults to navigate to row id). */
  onOpenFilesTab?: (row: Row) => void;
  /** Override reading `row.has_files` (e.g. nested field). */
  getHasFiles?: (row: Row) => boolean | undefined;
  flex?: number;
  field?: string;
};

/** Shared DataGrid column — drop in next to PDF / Modified on any module list. */
export function vaultFilesColumn<
  Row extends { id?: any; has_files?: boolean },
>(opts: FilesColumnOpts<Row>): GridColDef<Row> {
  return {
    field: opts.field ?? "_files",
    headerName: "Files",
    flex: opts.flex ?? 0.7,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams<Row>) => (
      <VaultFilesCell
        paths={opts.getPaths(params.row)}
        recordId={params.row.id}
        hasFiles={
          opts.getHasFiles
            ? opts.getHasFiles(params.row)
            : params.row.has_files
        }
        onOpenFilesTab={
          opts.onOpenFilesTab
            ? () => opts.onOpenFilesTab!(params.row)
            : undefined
        }
      />
    ),
  };
}
